import {Color} from '@benchling/nucleus-ui';
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {Edge, MarkerType, Node, NodeChange, ReactFlowInstance, XYPosition, useNodesState} from 'reactflow';

import {assert} from 'Asserts';
import {
  hideFlowchartElementBeforeLayout,
  unhideFlowchartElementAfterLayout,
} from 'ui-library/reactflow/layout/hideFlowchartElementBeforeLayoutUtils';
import {useControlledAutoLayout} from 'ui-library/reactflow/layout/useAutoLayout';

import {NodeType as GroupFlowchartNodeType, GROUP_TITLE_GAP} from './GroupFlowchartNode';
import {
  getDestinationNodeId,
  getHeightOfNodeColumn,
  getNodePositionsByPMEIIdForProcedureRun,
  makeDestinationPMEINode,
  makeIntraRunMethodEdges,
  makeUnitOpLinkEdge,
  NODE_HORIZONTAL_GAP,
  PROCEDURE_RUN_HORIZONTAL_GAP,
  PROCEDURE_RUN_VERTICAL_GAP,
  UnitOpLinkEdge,
} from './UnitOpLinkingFlowchartUtils';
import {UnitOperationFields} from './useUnitOpLinkingState';

const GROUP_TITLE_TOTAL_HEIGHT = GROUP_TITLE_GAP + 20; // Approximate height of title + gap

const NODE_GROUP_PADDING = 4;

export const INTRA_RUN_METHOD_EDGE_STYLE = {
  markerEnd: {type: MarkerType.Arrow, strokeWidth: 1.5, color: Color.GRAY_600},
  style: {strokeWidth: 1.5, stroke: Color.GRAY_600},
};

const HOVER_GROUP_NODE_ID_PREFIX = 'hover-group-';
const SELECTION_GROUP_NODE_ID_PREFIX = 'selection-group-';
const PROCEDURE_RUN_NODE_ID_PREFIX = 'procedure-run-';
const PROCEDURE_RUN_EDGE_ID_PREFIX = 'procedure-run-edge-';

export function makeHoverGroupNodeId(itemId: string): string {
  return `${HOVER_GROUP_NODE_ID_PREFIX}${itemId}`;
}

export function makeSelectionGroupNodeId(itemId: string): string {
  return `${SELECTION_GROUP_NODE_ID_PREFIX}${itemId}`;
}

export function isHoverGroupNodeId(nodeId: string): boolean {
  return nodeId.startsWith(HOVER_GROUP_NODE_ID_PREFIX);
}

export function isSelectionGroupNodeId(nodeId: string): boolean {
  return nodeId.startsWith(SELECTION_GROUP_NODE_ID_PREFIX);
}

function getItemIdFromGroupNodeId(nodeId: string): string {
  if (isHoverGroupNodeId(nodeId)) {
    return nodeId.slice(HOVER_GROUP_NODE_ID_PREFIX.length);
  }
  return nodeId.slice(SELECTION_GROUP_NODE_ID_PREFIX.length);
}

export const INTER_RUN_LINK_EDGE_STYLE = {
  markerEnd: {type: MarkerType.Arrow, strokeWidth: 1.5, color: Color.GRAY_600},
  style: {strokeWidth: 1.5, stroke: Color.GRAY_600, strokeDasharray: '4, 4'},
};

/**
 * Custom hook that automatically pans the viewport to fit the selected nodes whenever
 * the selection changes. The caller owns the reactFlowInstance ref and is responsible
 * for assigning it via ReactFlow's onInit callback.
 */
export function useAutoPanToSelection(
  reactFlowInstance: MutableRefObject<ReactFlowInstance | null>,
  isReactFlowReady: boolean,
  selectedIds: Array<string>,
  pmeiIdToUnitOperationFields: Map<string, UnitOperationFields>,
  methodIdToPMEIs: Map<string, Array<UnitOperationFields>>,
  procedureRunIdToPMEIs: Map<string, Array<UnitOperationFields>>
): void {
  useEffect(() => {
    if (!isReactFlowReady || selectedIds.length === 0) {
      return;
    }

    const instance = reactFlowInstance.current;
    assert(instance != null, 'ReactFlow instance must be set before isReactFlowReady is true');

    const nodeIdsToFit: Array<string> = [];

    for (const selectedId of selectedIds) {
      // Check if selected item is a PMEI (individual node)
      if (pmeiIdToUnitOperationFields.has(selectedId)) {
        nodeIdsToFit.push(getDestinationNodeId(selectedId));
        continue;
      }

      // Check if selected item is a method group
      const methodPMEIs = methodIdToPMEIs.get(selectedId);
      if (methodPMEIs) {
        nodeIdsToFit.push(...methodPMEIs.map((pmei) => getDestinationNodeId(pmei.pmeiId)));
        continue;
      }

      // Check if selected item is a procedure run
      const procedureRunPMEIs = procedureRunIdToPMEIs.get(selectedId);
      if (procedureRunPMEIs) {
        nodeIdsToFit.push(...procedureRunPMEIs.map((pmei) => getDestinationNodeId(pmei.pmeiId)));
      }
    }

    if (nodeIdsToFit.length > 0) {
      instance.fitView({
        nodes: nodeIdsToFit.map((id) => ({id})),
        padding: 0.2,
        duration: 800,
        includeHiddenNodes: false,
      });
    }
  }, [
    reactFlowInstance,
    isReactFlowReady,
    selectedIds,
    pmeiIdToUnitOperationFields,
    methodIdToPMEIs,
    procedureRunIdToPMEIs,
  ]);
}

/**
 * Custom hook to handle node creation and management for the flowchart.
 */
export function useFlowchartNodes(unitOperations: Array<UnitOperationFields>): {
  nodes: Array<Node>;
  setNodes: Dispatch<SetStateAction<Array<Node>>>;
  onNodesChange: (nodes: Array<NodeChange>) => void;
  nodeLayoutDepsHash: string;
} {
  const [nodes, setNodes, onNodesChange] = useNodesState(
    unitOperations.map((node) => makeDestinationPMEINode(node, {selectable: false}))
  );

  const nodeLayoutDepsHash = JSON.stringify(
    nodes
      .filter(
        (node) =>
          node.type !== GroupFlowchartNodeType.HOVER_GROUP &&
          node.type !== GroupFlowchartNodeType.SELECTION_GROUP
      )
      .map(({data: {pmeiId}, height, width}) => ({pmeiId, height, width}))
  );

  return {nodes, setNodes, onNodesChange, nodeLayoutDepsHash};
}

/**
 * Custom hook to handle edge creation and management for the flowchart.
 */
export function useFlowchartEdges(
  orderedPMEIsInColumnsPerProcedureRun: Array<Array<Array<UnitOperationFields>>>,
  interRunLinks: Array<UnitOpLinkEdge>
): Array<Edge<UnitOpLinkEdge>> {
  const intraRunEdges = useMemo(
    () =>
      orderedPMEIsInColumnsPerProcedureRun.flatMap((procedureRun) =>
        makeIntraRunMethodEdges(procedureRun).map((edge) => ({
          ...edge,
          ...INTRA_RUN_METHOD_EDGE_STYLE,
        }))
      ),
    [orderedPMEIsInColumnsPerProcedureRun]
  );

  const interRunEdges = useMemo(
    () =>
      interRunLinks.map((link) => ({
        ...makeUnitOpLinkEdge(link),
        source: getDestinationNodeId(link.sourcePMEIId),
        ...INTER_RUN_LINK_EDGE_STYLE,
      })),
    [interRunLinks]
  );

  return useMemo(() => [...intraRunEdges, ...interRunEdges], [intraRunEdges, interRunEdges]);
}

/**
 * Custom hook to handle node layout calculations for the flowchart.
 * Combines procedure run absolute positions with relative within-run positions
 * to produce final node positions, then applies them alongside group overlay nodes.
 */
export function useFlowchartLayout(
  setNodes: Dispatch<SetStateAction<Array<Node>>>,
  nodeLayoutDepsHash: string,
  hoveringItemId: string | null,
  selectedIds: Array<string>,
  createGroupNodes: (hoveringId: string | null, selectedIds: Array<string>) => Array<Node>,
  pmeiIdToUnitOperationFields: Map<string, UnitOperationFields>,
  methodIdToPMEIs: Map<string, Array<UnitOperationFields>>,
  procedureRunIdToPMEIs: Map<string, Array<UnitOperationFields>>,
  procedureRunAbsolutePositions: Record<string, XYPosition>,
  unitOpNodePositionsWithinRuns: Map<string, XYPosition>
): void {
  useLayoutEffect(() => {
    // All computation runs inside the setNodes functional updater, which always receives
    // the latest node state. This avoids needing `nodes` as a direct effect dependency
    // (which would cause an infinite loop since the effect calls setNodes).
    // nodeLayoutDepsHash still ensures the effect re-runs when node dimensions change.
    setNodes((currentNodes: Array<Node>) => {
      const finalNodePositions: Record<string, XYPosition> = {};

      // Combine procedure run positions + relative unit op positions = final positions
      currentNodes.forEach((node) => {
        const pmeiId = node.data.pmeiId;
        const relativePosition = unitOpNodePositionsWithinRuns.get(pmeiId);
        const unitOperationFields = pmeiIdToUnitOperationFields.get(pmeiId);

        if (!unitOperationFields || !relativePosition) {
          return;
        }

        const procedureRunId = unitOperationFields.procedureRunId;
        const procedureRunPosition = procedureRunAbsolutePositions[procedureRunId] ?? {x: 0, y: 0};

        finalNodePositions[pmeiId] = {
          x: procedureRunPosition.x + relativePosition.x,
          y: procedureRunPosition.y + relativePosition.y,
        };
      });

      const groupNodes = createGroupNodes(hoveringItemId, selectedIds);
      const positionedGroupNodes = positionGroupNodes(
        currentNodes,
        groupNodes,
        finalNodePositions,
        pmeiIdToUnitOperationFields,
        methodIdToPMEIs,
        procedureRunIdToPMEIs
      );

      const hasLayout = Object.keys(procedureRunAbsolutePositions).length > 0;

      const nonGroupNodes = currentNodes.filter(
        (node: Node) =>
          node.type !== GroupFlowchartNodeType.HOVER_GROUP &&
          node.type !== GroupFlowchartNodeType.SELECTION_GROUP
      );

      const unitOpNodesWithPositions = nonGroupNodes.map((node: Node) => {
        const positioned = {
          ...node,
          position: finalNodePositions[node.data.pmeiId] || {x: 0, y: 0},
        };
        return hasLayout
          ? unhideFlowchartElementAfterLayout(positioned)
          : hideFlowchartElementBeforeLayout(positioned);
      });

      // Don't render group overlays until there are procedure run absolute positions
      if (!hasLayout) {
        return unitOpNodesWithPositions;
      }

      // Sort so group nodes (parents) render before their children
      return [...positionedGroupNodes.sort(sortGroupNodes), ...unitOpNodesWithPositions];
    });
  }, [
    nodeLayoutDepsHash,
    hoveringItemId,
    selectedIds,
    createGroupNodes,
    pmeiIdToUnitOperationFields,
    methodIdToPMEIs,
    procedureRunIdToPMEIs,
    procedureRunAbsolutePositions,
    unitOpNodePositionsWithinRuns,
    setNodes,
  ]);
}

const PROCEDURE_RUN_NODE_TYPE = 'PROCEDURE_RUN';

/**
 * Returns relative positions for each unit op within its run (`unitOpNodePositionsWithinRuns`)
 * and absolute positions for each run as a whole (`procedureRunAbsolutePositions`).
 * Combine both in `useFlowchartLayout` to get each node's final absolute position.
 */
export function useProcedureRunLayout(
  orderedPMEIsInColumnsPerProcedureRun: Array<Array<Array<UnitOperationFields>>>,
  nodes: Array<Node>,
  nodeLayoutDepsHash: string,
  interRunLinks: Array<UnitOpLinkEdge>,
  pmeiIdToUnitOperationFields: Map<string, UnitOperationFields>
): {
  unitOpNodePositionsWithinRuns: Map<string, XYPosition>;
  procedureRunAbsolutePositions: Record<string, XYPosition>;
} {
  // Avoids listing `nodes` as an effect dep (which would cause a setNodes → re-run loop);
  // nodeLayoutDepsHash is the real signal for when dimensions change.
  // useLayoutEffect keeps the ref current before the layout effects below read it.
  const nodesRef = useRef(nodes);
  useLayoutEffect(() => {
    nodesRef.current = nodes;
  });

  // useLayoutEffect+setState (vs useMemo) keeps nodeLayoutDepsHash as the real dep
  // without adding `nodes`, and avoids a new Map identity on every render.
  const [unitOpNodePositionsWithinRuns, setUnitOpNodePositionsWithinRuns] = useState(
    () => new Map<string, XYPosition>()
  );

  useLayoutEffect(() => {
    const currentNodes = nodesRef.current;
    const positionMap = new Map<string, XYPosition>();

    for (const procedureRun of orderedPMEIsInColumnsPerProcedureRun) {
      if (procedureRun.length === 0 || procedureRun[0].length === 0) {
        continue;
      }

      const procedureRunId = procedureRun[0][0].procedureRunId;
      const nodesInProcedureRun = currentNodes.filter((node) => node.data.procedureRunId === procedureRunId);

      // Positions are relative to the procedure run origin (0, 0)
      const {positionsByPMEIId} = getNodePositionsByPMEIIdForProcedureRun(
        nodesInProcedureRun,
        procedureRun,
        0 // startingX = 0 (relative)
      );

      for (const [pmeiId, position] of Object.entries(positionsByPMEIId)) {
        positionMap.set(pmeiId, position);
      }
    }

    setUnitOpNodePositionsWithinRuns(positionMap);
  }, [orderedPMEIsInColumnsPerProcedureRun, nodeLayoutDepsHash]);

  // Virtual nodes/edges for DAG layout, sized to each run's bounding box.
  const [procedureRunVirtualNodes, setProcedureRunVirtualNodes] = useState<Array<Node>>(() => []);
  const [procedureRunVirtualEdges, setProcedureRunVirtualEdges] = useState<Array<Edge>>(() => []);

  useLayoutEffect(() => {
    const currentNodes = nodesRef.current;

    if (orderedPMEIsInColumnsPerProcedureRun.length === 0) {
      setProcedureRunVirtualNodes([]);
      setProcedureRunVirtualEdges([]);
      return;
    }

    const virtualNodes = orderedPMEIsInColumnsPerProcedureRun.map((procedureRun) => {
      const procedureRunId = procedureRun[0][0].procedureRunId;
      return createProcedureRunNode(procedureRunId, procedureRun, currentNodes);
    });

    const virtualEdges = createProcedureRunEdges(virtualNodes, interRunLinks, pmeiIdToUnitOperationFields);

    setProcedureRunVirtualNodes(virtualNodes);
    setProcedureRunVirtualEdges(virtualEdges);
  }, [orderedPMEIsInColumnsPerProcedureRun, nodeLayoutDepsHash, interRunLinks, pmeiIdToUnitOperationFields]);

  const [layoutedProcedureRunNodes, setLayoutedProcedureRunNodes] = useState<Array<Node>>([]);
  const [, setLayoutedProcedureRunEdges] = useState<Array<Edge>>([]);

  // Apply autolayout to position procedure runs relative to each other as a DAG
  useControlledAutoLayout(
    procedureRunVirtualNodes,
    procedureRunVirtualEdges,
    setLayoutedProcedureRunNodes,
    setLayoutedProcedureRunEdges,
    {
      horizontalSpaceBetweenNodes: PROCEDURE_RUN_HORIZONTAL_GAP,
    }
  );

  const procedureRunAbsolutePositions = useMemo(() => {
    const positions: Record<string, XYPosition> = {};
    layoutedProcedureRunNodes.forEach((node) => {
      if (node.data.procedureRunId) {
        positions[node.data.procedureRunId] = node.position;
      }
    });

    // Dagre lays out disconnected graph components independently, so source runs (no
    // incoming inter-run edges) that belong to different components may be assigned
    // different x positions and overlapping y positions.
    //
    // Step 1: flush all source runs to the same x (leftmost) so they form a left-aligned
    // starting column.
    // Step 2: after x-alignment, sort by y and push any vertically overlapping source runs
    // downward so their bounding boxes never intersect.
    const targetNodeIds = new Set(procedureRunVirtualEdges.map((e) => e.target));
    const sourceNodes = layoutedProcedureRunNodes.filter(
      (node) => node.data.procedureRunId && !targetNodeIds.has(node.id)
    );
    if (sourceNodes.length > 1) {
      const minX = Math.min(...sourceNodes.map((node) => node.position.x));
      sourceNodes.forEach((node) => {
        positions[node.data.procedureRunId] = {...positions[node.data.procedureRunId], x: minX};
      });

      const sortedSourceNodes = [...sourceNodes].sort((a, b) => a.position.y - b.position.y);
      // Seed from the topmost node so the first node always keeps its dagre y.
      let nextAvailableY = sortedSourceNodes[0].position.y;
      for (const node of sortedSourceNodes) {
        const y = Math.max(positions[node.data.procedureRunId].y, nextAvailableY);
        positions[node.data.procedureRunId] = {...positions[node.data.procedureRunId], y};
        nextAvailableY = y + (node.height ?? 0) + PROCEDURE_RUN_VERTICAL_GAP;
      }
    }

    return positions;
  }, [layoutedProcedureRunNodes, procedureRunVirtualEdges]);

  return {unitOpNodePositionsWithinRuns, procedureRunAbsolutePositions};
}

function positionGroupNodes(
  nodes: Array<Node>,
  groupNodes: Array<Node>,
  finalNodePositions: Record<string, XYPosition>,
  pmeiIdToUnitOperationFields: Map<string, UnitOperationFields>,
  methodIdToPMEIs: Map<string, Array<UnitOperationFields>>,
  procedureRunIdToPMEIs: Map<string, Array<UnitOperationFields>>
): Array<Node> {
  return groupNodes.map((groupNode: Node) => {
    let groupNodeIds: Array<string> = [];

    if (isHoverGroupNodeId(groupNode.id) || isSelectionGroupNodeId(groupNode.id)) {
      const groupId = getItemIdFromGroupNodeId(groupNode.id);

      const pmeiNode = pmeiIdToUnitOperationFields.get(groupId);
      const methodPMEIs = methodIdToPMEIs.get(groupId);
      const procedureRunPMEIs = procedureRunIdToPMEIs.get(groupId);
      if (pmeiNode) {
        groupNodeIds = [pmeiNode.pmeiId];
      } else if (methodPMEIs) {
        groupNodeIds = methodPMEIs.map((pmei) => pmei.pmeiId);
      } else if (procedureRunPMEIs) {
        groupNodeIds = procedureRunPMEIs.map((pmei) => pmei.pmeiId);
      } else {
        assert(
          false,
          `Group node ID "${groupNode.id}" does not correspond to a known PMEI, method, or procedure run`
        );
      }
    }

    if (groupNodeIds.length === 0) {
      return groupNode;
    }

    // Calculate bounding box based on all nodes in the group
    const childNodesInGroup = nodes.filter((node) => groupNodeIds.includes(node.data.pmeiId));
    const minX = Math.min(...childNodesInGroup.map((node) => finalNodePositions[node.data.pmeiId]?.x || 0));
    const maxX = Math.max(
      ...childNodesInGroup.map((node) => (finalNodePositions[node.data.pmeiId]?.x || 0) + (node.width || 0))
    );
    const minY = Math.min(...childNodesInGroup.map((node) => finalNodePositions[node.data.pmeiId]?.y || 0));
    const maxY = Math.max(
      ...childNodesInGroup.map((node) => (finalNodePositions[node.data.pmeiId]?.y || 0) + (node.height || 0))
    );

    return {
      ...groupNode,
      position: {x: minX - NODE_GROUP_PADDING, y: minY - NODE_GROUP_PADDING - GROUP_TITLE_TOTAL_HEIGHT}, // Account for title height
      data: {
        ...groupNode.data,
        width: maxX - minX + NODE_GROUP_PADDING * 2,
        height: maxY - minY + NODE_GROUP_PADDING * 2,
      },
    };
  });
}

/**
 * Calculate the total width of a procedure run including all its method columns and gaps.
 */
function getProcedureRunWidth(
  procedureRunNodes: Array<Node>,
  procedureRun: Array<Array<UnitOperationFields>>
): number {
  const nodeByPMEIId = new Map(procedureRunNodes.map((node) => [node.data.pmeiId, node]));
  let totalWidth = 0;

  for (const methodColumn of procedureRun) {
    const maxNodeWidth = Math.max(...methodColumn.map((pmei) => nodeByPMEIId.get(pmei.pmeiId)?.width ?? 0));
    totalWidth += maxNodeWidth;
  }
  totalWidth += (procedureRun.length - 1) * NODE_HORIZONTAL_GAP;

  return totalWidth;
}

/**
 * Calculate the total height of a procedure run as the maximum column height across
 * all method columns (columns are stacked vertically, so height = tallest column).
 */
function getProcedureRunHeight(
  procedureRunNodes: Array<Node>,
  procedureRun: Array<Array<UnitOperationFields>>
): number {
  const nodeByPMEIId = new Map(procedureRunNodes.map((node) => [node.data.pmeiId, node]));
  let maxHeight = 0;

  for (const methodColumn of procedureRun) {
    const columnNodes = methodColumn
      .map((pmei) => nodeByPMEIId.get(pmei.pmeiId))
      .filter((node): node is Node => node !== undefined);
    maxHeight = Math.max(maxHeight, getHeightOfNodeColumn(columnNodes));
  }

  return maxHeight;
}

/**
 * Create a virtual node representing an entire procedure run for DAG layout.
 * The node's width and height match the actual procedure run bounding box so
 * the DAG layout produces correctly-spaced absolute positions.
 */
function createProcedureRunNode(
  procedureRunId: string,
  procedureRun: Array<Array<UnitOperationFields>>,
  nodes: Array<Node>
): Node {
  const procedureRunPMEIIds = new Set(
    procedureRun.flatMap((methodColumn) => methodColumn.map((pmei) => pmei.pmeiId))
  );
  const procedureRunNodes = nodes.filter((node) => procedureRunPMEIIds.has(node.data.pmeiId));

  return {
    id: `${PROCEDURE_RUN_NODE_ID_PREFIX}${procedureRunId}`,
    type: PROCEDURE_RUN_NODE_TYPE,
    position: {x: 0, y: 0},
    data: {procedureRunId},
    width: getProcedureRunWidth(procedureRunNodes, procedureRun),
    height: getProcedureRunHeight(procedureRunNodes, procedureRun),
    selectable: false,
    deletable: false,
    draggable: false,
    connectable: false,
    hidden: true, // Hidden since it is only used for positioning
  };
}

/**
 * Create virtual edges between procedure run nodes for DAG layout.
 * Deduplicates connections (multiple inter-run links between the same runs become one edge).
 * When no inter-run links are present, no edges are created and procedure run nodes remain
 * disconnected for layout.
 */
function createProcedureRunEdges(
  procedureRunNodes: Array<Node>,
  interRunLinks: Array<UnitOpLinkEdge>,
  pmeiIdToUnitOperationFields: Map<string, UnitOperationFields>
): Array<Edge> {
  const procedureRunEdges: Array<Edge> = [];
  const procedureRunIdToNodeId = new Map(
    procedureRunNodes.map((node) => [node.data.procedureRunId, node.id])
  );
  const addedConnections = new Set<string>();

  for (const link of interRunLinks) {
    const sourcePMEI = pmeiIdToUnitOperationFields.get(link.sourcePMEIId);
    const destPMEI = pmeiIdToUnitOperationFields.get(link.destinationPMEIId);

    if (!sourcePMEI || !destPMEI || sourcePMEI.procedureRunId === destPMEI.procedureRunId) {
      continue;
    }

    const sourceNodeId = procedureRunIdToNodeId.get(sourcePMEI.procedureRunId);
    const destNodeId = procedureRunIdToNodeId.get(destPMEI.procedureRunId);

    if (!sourceNodeId || !destNodeId) {
      continue;
    }

    const connectionKey = `${sourceNodeId}-${destNodeId}`;
    if (!addedConnections.has(connectionKey)) {
      procedureRunEdges.push({
        id: `${PROCEDURE_RUN_EDGE_ID_PREFIX}${sourcePMEI.procedureRunId}-${destPMEI.procedureRunId}`,
        source: sourceNodeId,
        target: destNodeId,
        type: 'default',
      });
      addedConnections.add(connectionKey);
    }
  }

  return procedureRunEdges;
}

/**
 * Sort group nodes so that hover groups come after selection groups in the array,
 * ensuring hover groups are rendered on top of selection groups in ReactFlow.
 */
function sortGroupNodes(a: Node, b: Node): number {
  const aIsHover = isHoverGroupNodeId(a.id);
  const bIsHover = isHoverGroupNodeId(b.id);
  if (aIsHover && !bIsHover) {
    return 1;
  }
  if (!aIsHover && bIsHover) {
    return -1;
  }
  return 0;
}
